import { type RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ALL_TABS_INLINE, fitTabsToWidth, isSameTabFit, type TabFit } from "./tabOverflow";
import type { SystemTab } from "./utils";

/** Marks a tab in the bar with its key, so the bar can measure it. */
export const TAB_KEY_ATTRIBUTE = "data-system-tab-key";
/** Marks the bar's "More" tab. */
export const MORE_TAB_ATTRIBUTE = "data-system-tab-more";

/** A tab's key in the bar — what `TabFit.collapsed` lists. */
export const tabKeyOf = <T>(tab: SystemTab<T>): string => String(tab.value);

/** Rounded up, so a bar never decides a tab fits by a sub-pixel it doesn't have. */
const widthOf = (element: Element): number => Math.ceil(element.getBoundingClientRect().width);

/**
 * Measures the bar after every render and decides which collapsible tabs move into its "More" menu (`fitTabsToWidth`).
 * A tab's width is read while it is in the bar and remembered once it moves into "More", so the bar knows when there is
 * room to bring it back. Runs before paint, so a tab never flashes in the bar before it moves; re-measures when the bar
 * resizes. Off (`enabled` false): every tab stays in the bar and nothing is measured.
 */
export const useTabOverflow = <T>(tabs: readonly SystemTab<T>[], enabled: boolean): { rootRef: RefObject<HTMLDivElement | null>; fit: TabFit } => {
	const rootRef = useRef<HTMLDivElement | null>(null);
	const widths = useRef(new Map<string, number>());
	const moreWidth = useRef(0);
	const [fit, setFit] = useState<TabFit>(ALL_TABS_INLINE);
	// Only its change matters: a resize re-renders the bar, and the layout effect below measures again.
	const [, setResizeCount] = useState(0);

	// No dependency list on purpose: a label or badge can change any tab's width, so every render is measured. It
	// settles at once — the fit only changes when the widths call for it.
	useLayoutEffect(() => {
		const root = rootRef.current;
		if (!enabled || root === null) return;
		for (const element of root.querySelectorAll(`[${TAB_KEY_ATTRIBUTE}]`)) {
			const key = element.getAttribute(TAB_KEY_ATTRIBUTE);
			if (key !== null) widths.current.set(key, widthOf(element));
		}
		const more = root.querySelector(`[${MORE_TAB_ATTRIBUTE}]`);
		if (more !== null) moreWidth.current = widthOf(more);
		const measured = tabs.map((tab) => ({
			key: tabKeyOf(tab),
			// DEFAULT: a tab is in the bar (and so measured above) until a fit moves it out, so every tab has a width by
			// now; 0 only stands in for one that has never rendered.
			width: widths.current.get(tabKeyOf(tab)) ?? 0,
			collapsible: tab.collapsible === true,
		}));
		const next = fitTabsToWidth({ tabs: measured, moreWidth: moreWidth.current, available: root.clientWidth });
		setFit((current) => (isSameTabFit(current, next) ? current : next));
	});

	useEffect(() => {
		const root = rootRef.current;
		if (!enabled || root === null) return;
		const observer = new ResizeObserver(() => setResizeCount((count) => count + 1));
		observer.observe(root);
		return () => observer.disconnect();
	}, [enabled]);

	return { rootRef, fit: enabled ? fit : ALL_TABS_INLINE };
};
